import Icon from 'ui/Icon';
import { HStack, VStack } from 'ui/Layout';
import Popover from 'ui/Popover';
import { Text } from 'ui/Typography';

import { useSankeyExcelBindingContext } from '../../../../contexts/sankeyExcelBindingContext';
import { useSankeyFormContext } from '../../../../contexts/sankeyFormContext';
import { useSankeyRenderContext } from '../../../../contexts/sankeyRenderContext';
import {
  computeOriginalComparisonLine,
  computeOriginalLabelValue,
} from '../../../../diagram/labelValues';
import { SankeyNode } from '../../../../diagram/types';
import { useIsDataFromExcel } from '../../../DataEditor/utils';
import InfoTooltip from '../../../InfoTooltip';
import { FormColorSelect, FormToggleableTextInput } from '../../../SankeyForm';
import { useSankeyNodeInteraction } from '../../../SankeyInteractionProvider/utils';
import NodeAddFlowButton from './NodeAddFlowButton';
import NodeRemoveButton from './NodeRemoveButton';
import NodeRenameControl from './NodeRenameControl';

interface Props {
  node: SankeyNode;
  children: React.ReactNode;
}

// TODO: Use FieldSet Chakra component
const NodeEditPopover: React.FC<Props> = ({ node, children }) => {
  const { cfg, highestNodeAmount } = useSankeyRenderContext();
  const { isFocused, handleFocus, handleBlur } = useSankeyNodeInteraction(node);

  const { form } = useSankeyFormContext();
  const showLabels = form.watch('show_labels');
  const showComparisonLine = form.watch('show_comparison_line');
  const { hasExcelBindingInThisSheet } = useSankeyExcelBindingContext();
  const isDataFromExcel = useIsDataFromExcel();

  return (
    <Popover.Root
      lazyMount
      unmountOnExit
      open={isFocused}
      onOpenChange={(e) => (e.open ? handleFocus() : handleBlur())}
    >
      <Popover.Trigger>{children}</Popover.Trigger>
      <Popover.Content zIndex="docked">
        <Popover.Arrow />
        <Popover.Body>
          <Popover.Title>
            {hasExcelBindingInThisSheet ? (
              <Text>Edit node</Text>
            ) : isDataFromExcel ? (
              <Text>
                Edit node{' '}
                <Icon color="yellow.500" icon="triangle-exclamation" />
              </Text>
            ) : (
              <HStack>
                <Text flex={1}>Edit node</Text>
                <NodeAddFlowButton node={node} />
                <NodeRemoveButton node={node} />
              </HStack>
            )}
          </Popover.Title>
          <VStack alignItems="stretch">
            {isDataFromExcel && !hasExcelBindingInThisSheet && (
              <Text fontSize="xs">
                To edit the label change the values of the Excel cells that this
                diagram was created from.
              </Text>
            )}
            <HStack alignItems="flex-start">
              <NodeRenameControl node={node} />
              <FormColorSelect label="Color" field={`nodes.${node.id}.color`} />
            </HStack>
            {showLabels && (
              <FormToggleableTextInput
                label={
                  <>
                    2nd line custom text
                    <InfoTooltip label="Overwrites the text shown in the second line. Flows are not affected by this custom text." />
                  </>
                }
                fieldFontSize="xs"
                placeholder={computeOriginalLabelValue(
                  node,
                  cfg,
                  highestNodeAmount
                )}
                checkboxField={`nodes.${node.id}.use_custom_amount_label`}
                textField={`nodes.${node.id}.custom_amount_label`}
                size="sm"
              />
            )}
            {showLabels && showComparisonLine && (
              <FormToggleableTextInput
                label={
                  <>
                    3rd line custom text
                    <InfoTooltip label="Overwrites the text shown in the third line. Flows are not affected by this custom text." />
                  </>
                }
                fieldFontSize="xs"
                placeholder={computeOriginalComparisonLine(node, cfg)}
                checkboxField={`nodes.${node.id}.use_custom_comparison_label`}
                textField={`nodes.${node.id}.custom_comparison_label`}
                size="sm"
              />
            )}
          </VStack>
        </Popover.Body>
      </Popover.Content>
    </Popover.Root>
  );
};

export default NodeEditPopover;
